import type { AnalysisCardId } from "src-next/modules/clinical-insights/types/analysisTypes";

export interface InsightAnalysisTexts {
  itemSingular: string;
  itemPlural: string;
  rankingTitle: string;
  rankingHint: string;
  searchPlaceholder: string;
  rankingScopeAriaLabel: string;
  itemColumnLabel: string;
  emptyPatientItemText: string;
  emptyRowText: string;
  loadingText: string;
  busyVerb: string;
  errorText: string;
  actionGuideText: string;
  /** Prefixo da mensagem de lista vazia: "{emptyListPrefix} em {period} para {practitioner}" */
  emptyListPrefix: string;
  noItemsTooltip: string;
}

const CONFIG: Record<AnalysisCardId, InsightAnalysisTexts> = {
  condutas: {
    itemSingular: "conduta",
    itemPlural: "condutas",
    rankingTitle: "Condutas mais registradas",
    rankingHint:
      "Toque em uma conduta para filtrar pacientes. Use «Todas» para buscar na lista completa.",
    searchPlaceholder: "Buscar conduta...",
    rankingScopeAriaLabel: "Mostrar top 5 ou todas as condutas",
    itemColumnLabel: "Conduta",
    emptyPatientItemText: "Nenhuma conduta registrada para este paciente.",
    emptyRowText: "Sem condutas",
    loadingText: "Buscando condutas…",
    busyVerb: "Buscando condutas",
    errorText: "Não foi possível carregar as condutas.",
    actionGuideText:
      "Escolha situação ou conduta para ver os pacientes. Depois use Evoluir ou mande WhatsApp.",
    emptyListPrefix: "Nenhuma conduta",
    noItemsTooltip: "Sem condutas",
  },
  problemas: {
    itemSingular: "problema",
    itemPlural: "problemas",
    rankingTitle: "Problemas mais registrados",
    rankingHint:
      "Toque em um problema para filtrar pacientes. Use «Todas» para buscar na lista completa.",
    searchPlaceholder: "Buscar problema...",
    rankingScopeAriaLabel: "Mostrar top 5 ou todos os problemas",
    itemColumnLabel: "Problema",
    emptyPatientItemText: "Nenhum problema registrado para este paciente.",
    emptyRowText: "Sem problemas",
    loadingText: "Buscando problemas…",
    busyVerb: "Buscando problemas",
    errorText: "Não foi possível carregar os problemas.",
    actionGuideText:
      "Escolha situação ou problema para ver os pacientes. Depois use Evoluir ou mande WhatsApp.",
    emptyListPrefix: "Nenhum problema",
    noItemsTooltip: "Sem problemas",
  },
  planos: {
    itemSingular: "plano",
    itemPlural: "planos",
    rankingTitle: "Planos mais registrados",
    rankingHint:
      "Toque em um plano para filtrar pacientes. Use «Todas» para buscar na lista completa.",
    searchPlaceholder: "Buscar plano...",
    rankingScopeAriaLabel: "Mostrar top 5 ou todos os planos",
    itemColumnLabel: "Plano",
    emptyPatientItemText: "Nenhum plano registrado para este paciente.",
    emptyRowText: "Sem planos",
    loadingText: "Buscando planos…",
    busyVerb: "Buscando planos",
    errorText: "Não foi possível carregar os planos.",
    actionGuideText:
      "Escolha situação ou plano para ver os pacientes. Depois use Evoluir ou mande WhatsApp.",
    emptyListPrefix: "Nenhum plano",
    noItemsTooltip: "Sem planos",
  },
  tratamentos: {
    itemSingular: "prescrição",
    itemPlural: "prescrições",
    rankingTitle: "Medicamentos mais prescritos",
    rankingHint:
      "Toque em um medicamento para filtrar pacientes. Use «Todas» para buscar na lista completa.",
    searchPlaceholder: "Buscar medicamento...",
    rankingScopeAriaLabel: "Mostrar top 5 ou todas as prescrições",
    itemColumnLabel: "Medicamento",
    emptyPatientItemText: "Nenhuma prescrição registrada para este paciente.",
    emptyRowText: "Sem prescrições",
    loadingText: "Buscando prescrições…",
    busyVerb: "Buscando prescrições",
    errorText: "Não foi possível carregar as prescrições.",
    actionGuideText:
      "Escolha situação ou medicamento para ver os pacientes. Depois use Evoluir ou mande WhatsApp.",
    emptyListPrefix: "Nenhuma prescrição",
    noItemsTooltip: "Sem prescrições",
  },
  diagnosticos: {
    itemSingular: "diagnóstico",
    itemPlural: "diagnósticos",
    rankingTitle: "Diagnósticos mais registrados",
    rankingHint:
      "Toque em um diagnóstico para filtrar pacientes. Use «Todas» para buscar na lista completa.",
    searchPlaceholder: "Buscar diagnóstico...",
    rankingScopeAriaLabel: "Mostrar top 5 ou todos os diagnósticos",
    itemColumnLabel: "Diagnóstico",
    emptyPatientItemText: "Nenhum diagnóstico registrado para este paciente.",
    emptyRowText: "Sem diagnósticos",
    loadingText: "Buscando diagnósticos…",
    busyVerb: "Buscando diagnósticos",
    errorText: "Não foi possível carregar os diagnósticos.",
    actionGuideText:
      "Escolha situação ou diagnóstico para ver os pacientes. Depois use Evoluir ou mande WhatsApp.",
    emptyListPrefix: "Nenhum diagnóstico",
    noItemsTooltip: "Sem diagnósticos",
  },
  service: {
    itemSingular: "exame",
    itemPlural: "exames",
    rankingTitle: "Exames mais solicitados",
    rankingHint:
      "Toque em um exame para filtrar pacientes. Use «Todas» para buscar na lista completa.",
    searchPlaceholder: "Buscar exame...",
    rankingScopeAriaLabel: "Mostrar top 5 ou todos os exames",
    itemColumnLabel: "Exame",
    emptyPatientItemText: "Nenhum exame registrado para este paciente.",
    emptyRowText: "Sem exames",
    loadingText: "Buscando exames…",
    busyVerb: "Buscando exames",
    errorText: "Não foi possível carregar os exames.",
    actionGuideText:
      "Escolha situação ou exame para ver os pacientes. Depois use Evoluir ou mande WhatsApp.",
    emptyListPrefix: "Nenhum exame",
    noItemsTooltip: "Sem exames",
  },
  alergias: {
    itemSingular: "alergia",
    itemPlural: "alergias",
    rankingTitle: "Alérgenos mais registrados",
    rankingHint:
      "Toque em um alérgeno para filtrar pacientes. Use «Todas» para buscar na lista completa.",
    searchPlaceholder: "Buscar alérgeno...",
    rankingScopeAriaLabel: "Mostrar top 5 ou todas as alergias",
    itemColumnLabel: "Alérgeno",
    emptyPatientItemText: "Nenhuma alergia registrada para este paciente.",
    emptyRowText: "Sem alergias",
    loadingText: "Buscando alergias…",
    busyVerb: "Buscando alergias",
    errorText: "Não foi possível carregar as alergias.",
    actionGuideText:
      "Escolha situação, criticidade ou alérgeno para ver os pacientes. Depois use Evoluir ou mande WhatsApp.",
    emptyListPrefix: "Nenhuma alergia",
    noItemsTooltip: "Sem alergias",
  },
  protocolos: {
    itemSingular: "protocolo",
    itemPlural: "protocolos",
    rankingTitle: "Protocolos mais utilizados",
    rankingHint:
      "Toque em um protocolo para filtrar pacientes. Use «Todas» para buscar na lista completa.",
    searchPlaceholder: "Buscar protocolo...",
    rankingScopeAriaLabel: "Mostrar top 5 ou todos os protocolos",
    itemColumnLabel: "Protocolo",
    emptyPatientItemText: "Nenhum protocolo registrado para este paciente.",
    emptyRowText: "Sem protocolos",
    loadingText: "Buscando protocolos…",
    busyVerb: "Buscando protocolos",
    errorText: "Não foi possível carregar os protocolos.",
    actionGuideText:
      "Escolha um protocolo para ver os pacientes. Depois use Evoluir ou mande WhatsApp.",
    emptyListPrefix: "Nenhum protocolo",
    noItemsTooltip: "Sem protocolos",
  },
};

export function getInsightAnalysisTexts(id: AnalysisCardId): InsightAnalysisTexts {
  return CONFIG[id];
}
